import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParty } from '../contexts/PartyContext';
import { ActiveMicrophoneSession } from '../types';
import TestTonePanel from './audio/TestTonePanel';
import HostMicrophoneControl, { HostMicrophoneControlData } from './audio/HostMicrophoneControl';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../firebase';
import { doc, onSnapshot, setDoc, serverTimestamp, writeBatch, collection } from 'firebase/firestore';
import { useSubscription } from '../hooks/useSubscription';
import AnalyticsService from '../services/AnalyticsService';
import { endPartyClient } from '../services/partyLifecycle';

interface HostControlsModalProps {
  isOpen: boolean;
  onClose: () => void;
  partyId?: string;
  activeMicrophones?: ActiveMicrophoneSession[];
  onDisconnectMicrophone?: (userId: string) => Promise<void> | void;
  microphoneControls?: HostMicrophoneControlData[];
  masterVolume?: number;
  onMasterVolumeChange?: (value: number) => void;
  onMicrophoneVolumeChange?: (userId: string, volume: number) => void;
  onMicrophoneMuteToggle?: (userId: string) => void;
  onMicrophonePanChange?: (userId: string, pan: number) => void;
  selectedAudioDevice?: string | null;
  onAudioDeviceChange?: (deviceId: string) => void;
  pendingUnmuteRequests?: string[];
  onApproveUnmuteRequest?: (guestId: string) => Promise<void> | void;
  pendingUnmuteDisplayNames?: Record<string, string>;
  onRequestReconnect?: (guestId: string) => void;
  reconnectStatusMap?: Record<string, 'idle' | 'pending' | 'sent' | 'succeeded' | 'failed'>;
  splashCountdown: number;
  onSplashCountdownChange: (value: number) => void;
  splashPaused: boolean;
  onSplashPausedChange: (paused: boolean) => void;
}

const HostControlsModal: React.FC<HostControlsModalProps> = ({
  isOpen,
  onClose,
  partyId,
  activeMicrophones,
  onDisconnectMicrophone,
  microphoneControls,
  masterVolume,
  onMasterVolumeChange,
  onMicrophoneVolumeChange,
  onMicrophoneMuteToggle,
  onMicrophonePanChange,
  selectedAudioDevice,
  onAudioDeviceChange,
  pendingUnmuteRequests,
  onApproveUnmuteRequest,
  pendingUnmuteDisplayNames,
  onRequestReconnect,
  reconnectStatusMap,
  // phoneMicEnabled,
  // onPhoneMicEnabledChange,
  splashCountdown,
  onSplashCountdownChange,
  splashPaused,
  onSplashPausedChange,
}) => {
  const { songQueue, currentSong, currentParty } = useParty();
  const navigate = useNavigate();
  const subscription = useSubscription();
  
  // Determine if this is a karaoke party or game party
  const isKaraokeParty = !currentParty?.gameMode || currentParty.gameMode === 'karaoke';
  const isGameParty = currentParty?.gameMode === 'farkle' || currentParty?.gameMode === 'imposter';
  
  // Default tab based on party type
  const [activeTab, setActiveTab] = useState<'audio' | 'video' | 'queue' | 'sources' | 'games'>(
    isGameParty ? 'games' : 'audio'
  );
  
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceSelection, setDeviceSelection] = useState<string>('');
  const [externalDisplayUrl, setExternalDisplayUrl] = useState<string>('');
  const [internalMasterVolume, setInternalMasterVolume] = useState<number>(80);
  const [isScanning, setIsScanning] = useState(false);
  const [processingQueue, setProcessingQueue] = useState(false);
  const [extVolume, setExtVolume] = useState<number>(100);
  const [extMuted, setExtMuted] = useState<boolean>(false);
  // Mic capture profile: quality vs low-latency (party-level)
  // Default to low-latency unless explicitly set otherwise in party control doc
  const [micProfile, setMicProfile] = useState<'quality' | 'low-latency'>('low-latency');
  // Phone mic toggle removed; party type determines mic support
  // Venue Ad scheduling (Phase 1)
  const [venueAdScheduled, setVenueAdScheduled] = useState<{
    status: 'scheduled' | 'playing' | 'played' | 'cancelled';
    durationSeconds?: number;
  } | null>(null);
  // Local Library (host-only, no upload)
  const [libraryConnected, setLibraryConnected] = useState(false);
  const [libraryBusy, setLibraryBusy] = useState(false);
  const [libraryQuery, setLibraryQuery] = useState('');
  const [libraryItems, setLibraryItems] = useState<Array<{ key: string; name: string; artist?: string; handle: any }>>([]);
  const [computeDurations, setComputeDurations] = useState(false);

  const resolvedPartyId = currentParty?.id ?? partyId ?? null;
  const microphoneSessions = activeMicrophones ?? [];
  const disconnectMicrophoneHandler = onDisconnectMicrophone ?? (() => Promise.resolve());
  const microphoneControlList = microphoneControls ?? microphoneSessions.map((session) => ({
    ...session,
    stream: null,
    analyser: null,
    level: 0,
    peak: 0,
    volume: 100,
    muted: false,
    pan: 0,
    reconnectStatus: reconnectStatusMap ? (reconnectStatusMap[session.userId] ?? 'idle') : 'idle',
  }));
  const masterVolumeValue = masterVolume ?? internalMasterVolume;

  useEffect(() => {
    if (isOpen) {
      void loadAudioDevices();
      if (resolvedPartyId) {
        setExternalDisplayUrl(`https://qroke-a6fed.firebaseapp.com/party/${resolvedPartyId}?display=1`);
      } else {
        setExternalDisplayUrl('');
      }
      // read persisted external volume/mute
      try {
        const volKey = `qroke:ytv:${resolvedPartyId}:volume`;
        const muteKey = `qroke:ytv:${resolvedPartyId}:muted`;
        const v = localStorage.getItem(volKey);
        const m = localStorage.getItem(muteKey);
        if (v !== null) setExtVolume(Math.max(0, Math.min(100, Number(v))));
        if (m !== null) setExtMuted(m === '1');
      } catch {}
      // subscribe to venue ad scheduled doc for UI reflection
      if (resolvedPartyId) {
        const ref = doc(db, 'parties', resolvedPartyId, 'control', 'scheduledAd');
        const unsub = onSnapshot(ref, (snap) => {
          if (snap.exists()) {
            const d = snap.data() as any;
            setVenueAdScheduled({ status: d.status ?? 'scheduled', durationSeconds: d.durationSeconds });
          } else {
            setVenueAdScheduled(null);
          }
        });
        return () => unsub();
      }
    }
  }, [isOpen, resolvedPartyId]);

  // Subscribe to mic profile setting for current party
  useEffect(() => {
    if (!isOpen || !resolvedPartyId) return;
    const ref = doc(db, 'parties', resolvedPartyId, 'control', 'micProfile');
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const d = snap.data() as any;
        const mode = (d.mode === 'low-latency') ? 'low-latency' : 'quality';
        setMicProfile(mode);
      } else {
        // When no document exists, prefer low-latency by default
        setMicProfile('low-latency');
      }
    });
    return () => unsub();
  }, [isOpen, resolvedPartyId]);

  const updateMicProfile = async (mode: 'quality' | 'low-latency') => {
    if (!resolvedPartyId) return;
    try {
      setMicProfile(mode);
      await setDoc(doc(db, 'parties', resolvedPartyId, 'control', 'micProfile'), { mode }, { merge: true });
    } catch (e) {
      // non-fatal
    }
  };

  useEffect(() => {
    if (selectedAudioDevice === null || selectedAudioDevice === undefined) {
      setDeviceSelection('');
    } else {
      setDeviceSelection(selectedAudioDevice);
    }
  }, [selectedAudioDevice]);

  const loadAudioDevices = async () => {
    try {
      // Request permissions first
      await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioOutputs = devices.filter(device => device.kind === 'audiooutput');
      setAudioDevices(audioOutputs);
      
      // Set default device if none selected
      if (!deviceSelection && audioOutputs.length > 0) {
        const nextDevice = audioOutputs[0].deviceId;
        setDeviceSelection(nextDevice);
        onAudioDeviceChange?.(nextDevice);
      }
    } catch (error) {
      // Error loading audio devices
    }
  };

  const handleAudioDeviceChange = async (deviceId: string) => {
    setDeviceSelection(deviceId);
    onAudioDeviceChange?.(deviceId);
    
    // Apply to current audio context if available
    try {
      const audioElements = document.querySelectorAll('audio, video');
      audioElements.forEach((element: any) => {
        if (element.setSinkId) {
          element.setSinkId(deviceId).catch((err: any) => {
            // Failed to set audio output
          });
        }
      });
    } catch (error) {
      // Error changing audio device
    }
  };

  const copyExternalDisplayUrl = () => {
    navigator.clipboard.writeText(externalDisplayUrl);
    alert('External display URL copied to clipboard!');
  };

  const openExternalDisplay = () => {
    try {
      if (resolvedPartyId) {
        // Mark that the host explicitly requested external display playback
        void setDoc(doc(db, 'parties', resolvedPartyId, 'control', 'displayControl'), {
          externalRequested: true,
          requestedAt: serverTimestamp(),
        }, { merge: true });
      }
    } catch {}
    window.open(externalDisplayUrl, '_blank', 'fullscreen=yes,scrollbars=no,toolbar=no,location=no,status=no,menubar=no');
  };

  const scanForDevices = async () => {
    setIsScanning(true);
    try {
      // Refresh device list
      await loadAudioDevices();
      setTimeout(() => setIsScanning(false), 1000);
    } catch (error) {
      setIsScanning(false);
    }
  };

  const handleMasterVolumeChangeInternal = (value: number) => {
    if (onMasterVolumeChange) {
      onMasterVolumeChange(value);
    } else {
      setInternalMasterVolume(value);
    }
  };

  const handleMicVolumeChange = (userId: string, volume: number) => {
    onMicrophoneVolumeChange?.(userId, volume);
  };

  const handleMicMuteToggle = (userId: string) => {
    onMicrophoneMuteToggle?.(userId);
  };

  const handleMicPanChange = (userId: string, pan: number) => {
    onMicrophonePanChange?.(userId, pan);
  };

  // Lightweight inline reconnect as a fallback when parent doesn't provide onRequestReconnect
  const handleInlineReconnect = async (guestId: string) => {
    try {
      const signalingFn = httpsCallable(functions, 'webrtcSignaling');
      await signalingFn({ data: { type: 'request-offer', to: guestId } });
      alert('Requested reconnect from guest');
    } catch (e) {
      alert('Failed to request reconnect');
    }
  };

  // Queue Management Functions
  const handleRemoveSong = async (songId: string) => {
    if (!resolvedPartyId || processingQueue) return;
    const payload = {
      action: 'queue',
      partyId: resolvedPartyId,
      queueAction: 'remove',
      songId
    };
    try {
      setProcessingQueue(true);
      const partyFunction = httpsCallable(functions, 'party');
      await partyFunction(payload);
    } catch (error) {
      alert('Failed to remove song');
    } finally {
      setProcessingQueue(false);
    }
  };

  const handleClearQueue = async () => {
    if (!resolvedPartyId || processingQueue) return;
    if (!confirm('Are you sure you want to clear the entire queue?')) return;
    const payload = {
      action: 'queue',
      partyId: resolvedPartyId,
      queueAction: 'clear'
    };
    try {
      setProcessingQueue(true);
      const partyFunction = httpsCallable(functions, 'party');
      await partyFunction(payload);
    } catch (error) {
      alert('Failed to clear queue');
    } finally {
      setProcessingQueue(false);
    }
  };

  const handleShuffleQueue = async () => {
    if (!resolvedPartyId || processingQueue) return;
    const payload = {
      action: 'queue',
      partyId: resolvedPartyId,
      queueAction: 'shuffle'
    };
    try {
      setProcessingQueue(true);
      const partyFunction = httpsCallable(functions, 'party');
      await partyFunction(payload);
    } catch (error) {
      alert('Failed to shuffle queue');
    } finally {
      setProcessingQueue(false);
    }
  };

  // End party button
  const handleEndParty = async () => {
    if (!resolvedPartyId) return;
    try {
      await endPartyClient(resolvedPartyId);
      window.location.href = '/';
    } catch (e) {
      alert('Failed to end party.');
    }
  };

  const handleSkipSong = async () => {
    if (!resolvedPartyId || processingQueue) return;
    
    try {
      setProcessingQueue(true);
      const partyFunction = httpsCallable(functions, 'party');
      await partyFunction({
        action: 'next',
        partyId: resolvedPartyId
      });
    } catch (error) {
      alert('Failed to skip song');
    } finally {
      setProcessingQueue(false);
    }
  };

  const handleMoveSong = async (songId: string, newPosition: number) => {
    if (!resolvedPartyId || processingQueue) return;
    const payload = {
      action: 'queue',
      partyId: resolvedPartyId,
      queueAction: 'move',
      songId,
      newPosition
    };
    try {
      setProcessingQueue(true);
      const partyFunction = httpsCallable(functions, 'party');
      await partyFunction(payload);
    } catch (error) {
      alert('Failed to move song');
    } finally {
      setProcessingQueue(false);
    }
  };

  const launchFarklePlaytest = (mode: 'same-tab' | 'new-tab') => {
    if (!resolvedPartyId) {
      alert('Create or join a party to launch a playtest.');
      return;
    }
    const path = `/party/${resolvedPartyId}/game/farkle`;
    if (mode === 'new-tab') {
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }
    onClose();
    navigate(path);
  };

  // Venue Ads: schedule/cancel for next break
  const scheduleVenueAdNextBreak = async () => {
    if (!resolvedPartyId) return;
    try {
      const ref = doc(db, 'parties', resolvedPartyId, 'control', 'scheduledAd');
      await setDoc(ref, {
        type: 'venue',
        status: 'scheduled',
        requestedAt: serverTimestamp(),
        durationSeconds: 25,
      }, { merge: true });
      alert('Venue ad scheduled for the next song break.');
    } catch (e) {
      alert('Failed to schedule venue ad.');
    }
  };

  const cancelScheduledVenueAd = async () => {
    if (!resolvedPartyId) return;
    try {
      const ref = doc(db, 'parties', resolvedPartyId, 'control', 'scheduledAd');
      await setDoc(ref, { status: 'cancelled', cancelledAt: serverTimestamp() }, { merge: true });
      alert('Scheduled venue ad cancelled.');
    } catch (e) {
      alert('Failed to cancel venue ad.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto text-white">
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 className="text-3xl font-bold">🎛️ Host Controls</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-3xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-700">
          {isKaraokeParty && (
            <>
              <button
                onClick={() => setActiveTab('audio')}
                className={`px-6 py-4 font-semibold transition-colors ${
                  activeTab === 'audio' 
                    ? 'bg-qr-primary text-white border-b-2 border-qr-primary' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                🔊 Audio
              </button>
              <button
                onClick={() => setActiveTab('video')}
                className={`px-6 py-4 font-semibold transition-colors ${
                  activeTab === 'video' 
                    ? 'bg-qr-primary text-white border-b-2 border-qr-primary' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                📺 External Display
              </button>
              <button
                onClick={() => setActiveTab('queue')}
                className={`px-6 py-4 font-semibold transition-colors ${
                  activeTab === 'queue' 
                    ? 'bg-qr-primary text-white border-b-2 border-qr-primary' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                📋 Queue Management
              </button>
              <button
                onClick={() => setActiveTab('sources')}
                className={`px-6 py-4 font-semibold transition-colors ${
                  activeTab === 'sources' 
                    ? 'bg-qr-primary text-white border-b-2 border-qr-primary' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                📚 Sources
              </button>
            </>
          )}
          <button
            onClick={() => setActiveTab('games')}
            className={`px-6 py-4 font-semibold transition-colors ${
              activeTab === 'games'
                ? 'bg-qr-primary text-white border-b-2 border-qr-primary'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            🐸 Games Lab
          </button>
        </div>

        <div className="p-6">
          {/* Audio Tab */}
          {activeTab === 'audio' && (
            <div className="space-y-6">
              {/* Mic capture profile */}
              <div className="bg-gray-700/70 border border-gray-600 rounded-lg p-5 space-y-3">
                <h3 className="text-lg font-semibold text-white">Mic Capture Profile</h3>
                <p className="text-xs text-gray-300">Switch phone mic between Quality (AEC/NS/AGC on) and Low Latency (minimal processing). Phones will auto-adjust on the fly.</p>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="radio" name="micProfile" checked={micProfile === 'quality'} onChange={() => updateMicProfile('quality')} />
                    Quality
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="radio" name="micProfile" checked={micProfile === 'low-latency'} onChange={() => updateMicProfile('low-latency')} />
                    Low Latency
                  </label>
                </div>
              </div>
              {/* Phone mic toggle removed; party type determines mic support */}
              <div className="bg-gray-700/70 border border-gray-600 rounded-lg p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Phone Microphones</h3>
                    <p className="text-xs text-gray-300">Manage guest audio with live meters, mute, and balance controls.</p>
                  </div>
                  <span className="text-sm text-gray-300">{microphoneControlList.length} active</span>
                </div>
                {microphoneControlList.length === 0 ? (
                  <div className="bg-black/30 border border-dashed border-gray-500 text-gray-300 text-sm rounded-lg px-4 py-6 text-center">
                    No viewers are connected right now. When someone opens the My Songs panel and activates their mic, controls will appear here.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {microphoneControlList.map((control) => (
                      <HostMicrophoneControl
                        key={control.userId}
                        data={control}
                        onVolumeChange={onMicrophoneVolumeChange ?? (() => undefined)}
                        onMuteToggle={onMicrophoneMuteToggle ?? (() => undefined)}
                        onPanChange={onMicrophonePanChange ?? (() => undefined)}
                        onDisconnect={(userId) => void disconnectMicrophoneHandler(userId)}
                        onTrackToggle={() => {}}
                        onReconnect={(userId) => (onRequestReconnect ? onRequestReconnect(userId) : handleInlineReconnect(userId))}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Pending unmute requests from guests */}
              {pendingUnmuteRequests && pendingUnmuteRequests.length > 0 && (
                <div className="bg-yellow-800/30 border border-yellow-600 rounded-lg p-5 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-white">🔔 Pending Unmute Requests</h3>
                      <p className="text-xs text-gray-300">Approve a request to tell the guest to unmute their mic.</p>
                    </div>
                    <span className="text-sm text-gray-300">{pendingUnmuteRequests.length} pending</span>
                  </div>

                  <div className="space-y-2">
                    {pendingUnmuteRequests.map((guestId) => (
                      <div key={guestId} className="flex items-center justify-between bg-yellow-900/10 p-3 rounded">
                        <div className="text-sm text-white truncate">{pendingUnmuteDisplayNames?.[guestId] ?? guestId}</div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => onApproveUnmuteRequest && onApproveUnmuteRequest(guestId)}
                            className="bg-green-600 hover:bg-green-700 px-3 py-1 rounded text-white text-sm"
                          >
                            ✅ Approve
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-gray-700/70 border border-gray-600 rounded-lg p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Master Output</h3>
                  <span className="text-sm text-gray-300">{masterVolumeValue}%</span>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-gray-400">🔈</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={masterVolumeValue}
                    onChange={(event) => {
                      const nextValue = Number(event.target.value);
                      if (onMasterVolumeChange) {
                        onMasterVolumeChange(nextValue);
                      } else {
                        setInternalMasterVolume(nextValue);
                      }
                    }}
                    className="flex-1 accent-emerald-400"
                  />
                  <span className="text-gray-400">🔊</span>
                </div>
                <p className="text-[11px] text-gray-400">
                  Adjusts the overall mix sent to your speakers. Individual mic faders stay relative to this value.
                </p>
              </div>

              <div className="bg-gray-700/70 border border-gray-600 rounded-lg p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Audio Output Device</h3>
                  <button
                    onClick={scanForDevices}
                    disabled={isScanning}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    {isScanning ? '🔍 Scanning...' : '🔍 Scan Devices'}
                  </button>
                </div>
                <p className="text-xs text-gray-400">Select where the mixed audio should play. Pair Bluetooth speakers in system settings first, then refresh devices.</p>
                <select
                  value={deviceSelection}
                  onChange={(e) => handleAudioDeviceChange(e.target.value)}
                  className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-qr-primary focus:outline-none"
                >
                  <option value="">Default Audio Device</option>
                  {audioDevices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Audio Device ${device.deviceId.slice(0, 8)}`}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-blue-900/30 border border-blue-600/50 rounded-lg p-4">
                <h4 className="font-semibold text-blue-200 mb-2">💡 Audio Tips</h4>
                <ul className="text-blue-100 text-sm space-y-1">
                  <li>• Connect wired speakers via headphone jack or USB</li>
                  <li>• Use Bluetooth for wireless speakers (pair in system settings first)</li>
                  <li>• WiFi speakers may have delay - test before the party</li>
                  <li>• For best quality, use wired connections when possible</li>
                </ul>
              </div>
            </div>
          )}

          {/* Video Tab */}
          {activeTab === 'video' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-4">External Display Setup</h3>
                <p className="text-gray-300 mb-4">
                  Set up a separate screen to show the karaoke display while keeping your host controls private.
                </p>
                
                <div className="bg-gray-700 p-4 rounded-lg mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    External Display URL:
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={externalDisplayUrl}
                      readOnly
                      className="flex-1 p-3 bg-gray-600 text-white rounded-lg border border-gray-500"
                    />
                    <button
                      onClick={copyExternalDisplayUrl}
                      className="bg-green-600 hover:bg-green-700 px-4 py-3 rounded-lg font-medium transition-colors"
                    >
                      📋 Copy
                    </button>
                  </div>
                </div>

                <div className="flex space-x-4 mb-6">
                  <button
                    onClick={openExternalDisplay}
                    className="bg-qr-primary hover:bg-qr-secondary px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    🖥️ Open External Display
                  </button>
                </div>

                {/* Force host-side YouTube volume (applies to external window) */}
                <div className="bg-gray-700/70 border border-gray-600 rounded-lg p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-white">Track (YouTube) Volume</h3>
                    <span className="text-sm text-gray-300">{extVolume}%</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-gray-400">🔈</span>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={extVolume}
                      onChange={(e) => {
                        const next = Number(e.target.value);
                        setExtVolume(next);
                        try {
                          const key = `qroke:ytv:${resolvedPartyId}:volume`;
                          localStorage.setItem(key, String(next));
                        } catch {}
                      }}
                      className="flex-1 accent-purple-400"
                    />
                    <span className="text-gray-400">🔊</span>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-white">
                    <input
                      type="checkbox"
                      checked={extMuted}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setExtMuted(checked);
                        try {
                          const key = `qroke:ytv:${resolvedPartyId}:muted`;
                          localStorage.setItem(key, checked ? '1' : '0');
                        } catch {}
                      }}
                    />
                    Mute External Window
                  </label>
                  <p className="text-[11px] text-gray-400">This controls the YouTube player volume on the external display window using same‑origin messaging.</p>
                </div>

                {/* Audio routing help and test tone */}
                <TestTonePanel
                  devices={audioDevices}
                  selectedDeviceId={deviceSelection}
                  onDeviceChange={(id) => void handleAudioDeviceChange(id)}
                />
              </div>

              <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4">
                <h4 className="font-semibold text-purple-200 mb-2">🖥️ Display Setup Guide</h4>
                <ol className="text-purple-100 text-sm space-y-1 list-decimal list-inside">
                  <li>Connect your TV/projector to the same WiFi network</li>
                  <li>Open a web browser on the external display device</li>
                  <li>Navigate to the URL above or scan the QR code</li>
                  <li>Press F11 for fullscreen mode</li>
                  <li>External screen shows karaoke; your screen shows controls</li>
                </ol>
              </div>

              <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-200 mb-2">⚠️ Important Notes</h4>
                <ul className="text-yellow-100 text-sm space-y-1">
                  <li>• External display will show as a "VIEWER" (no audio conflicts)</li>
                  <li>• Only the host device controls audio and song progression</li>
                  <li>• Both displays stay perfectly synchronized</li>
                  <li>• Host can manage queue without affecting external display</li>
                </ul>
              </div>
            </div>
          )}

          {/* Sources Tab */}
          {activeTab === 'sources' && (
            <div className="space-y-6">
              <div className="bg-white/5 bg-opacity-5 rounded-lg border border-white/10 p-4">
                <h3 className="text-xl font-semibold mb-2">Streaming & Local Sources</h3>
                <div className="text-sm text-yellow-200 bg-yellow-900/30 border border-yellow-600/40 rounded px-3 py-2">
                  YouTube integration is for personal, at‑home use only. Commercial streaming may violate provider terms and copyright law. <a className="underline" href="/content-sources" target="_blank" rel="noopener">Learn more</a>
                </div>
              </div>

              {/* Local Library Connector */}
              <div className="bg-gray-700/70 border border-gray-600 rounded-lg p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-semibold text-white">Local Song Repository (Host)</h4>
                    <p className="text-xs text-gray-300">Browse and play songs directly from a folder on this device. Files never upload.</p>
                  </div>
                  <button
                    onClick={async () => {
                      if (!('showDirectoryPicker' in window)) {
                        alert('Folder access is supported in Chromium-based browsers. Try Chrome or Edge.');
                        return;
                      }
                      try {
                        setLibraryBusy(true);
                        // @ts-ignore
                        const dir: any = await (window as any).showDirectoryPicker();
                        const collected: Array<{ key: string; name: string; artist?: string; handle: any }> = [];
                        const walk = async (handle: any, path: string[] = []) => {
                          // @ts-ignore
                          for await (const [name, entry] of (handle as any).entries()) {
                            if (entry.kind === 'directory') {
                              await walk(entry as any, [...path, name]);
                            } else if (entry.kind === 'file') {
                              const lower = name.toLowerCase();
                              if (lower.endsWith('.mp3') || lower.endsWith('.ogg') || lower.endsWith('.wav')) {
                                const base = name.replace(/\.[^.]+$/, '');
                                // naive parse: Artist - Title
                                const parts = base.split(' - ');
                                const artist = parts.length > 1 ? parts[0] : undefined;
                                const title = parts.length > 1 ? parts.slice(1).join(' - ') : base;
                                collected.push({ key: [...path, name].join('/'), name: title, artist, handle: entry as any });
                              }
                            }
                          }
                        };
                        await walk(dir);
                        setLibraryItems(collected.sort((a,b) => a.name.localeCompare(b.name)));
                        setLibraryConnected(true);
                        try {
                          // Expose in a global map for host playback by ID
                          const map = new Map<string, any>();
                          collected.forEach(item => map.set(item.key, item.handle));
                          (window as any).qrokeLocalLibrary = map;
                        } catch {}
                      } catch (e) {
                        // user cancelled or error
                      } finally {
                        setLibraryBusy(false);
                      }
                    }}
                    disabled={libraryBusy}
                    className="bg-qr-primary hover:bg-qr-secondary disabled:bg-gray-600 px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    {libraryBusy ? 'Scanning…' : (libraryConnected ? 'Reselect Folder' : 'Connect Folder')}
                  </button>
                </div>

                {libraryConnected ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={libraryQuery}
                        onChange={(e) => setLibraryQuery(e.target.value)}
                        placeholder="Search local songs…"
                        className="flex-1 p-2 rounded bg-gray-800 text-white border border-gray-600 focus:outline-none focus:border-qr-primary"
                      />
                      <label className="flex items-center gap-2 text-xs text-gray-200 bg-black/30 px-2 py-1 rounded border border-gray-600" title="Attempts filename parse first; if checked, decodes small files to estimate duration (slower)">
                        <input type="checkbox" checked={computeDurations} onChange={(e) => setComputeDurations(e.target.checked)} />
                        Compute durations (slower)
                      </label>
                      <button
                        onClick={async () => {
                          if (!resolvedPartyId) return;
                          try {
                            const items = libraryItems;
                            const batch = writeBatch(db);
                            const colRef = collection(db, 'parties', resolvedPartyId, 'localCatalog');
                            // Clear existing by writing tombstones? For simplicity, overwrite up to 1000 entries
                            // Add/merge entries
                            const limit = Math.min(items.length, 1000);
                            const parseDurationFromName = (name: string): number | null => {
                              const m = name.match(/(\b|\()([0-5]?\d):([0-5]\d)(\b|\))/);
                              if (m) {
                                const min = parseInt(m[2], 10);
                                const sec = parseInt(m[3], 10);
                                return min * 60 + sec;
                              }
                              return null;
                            };

                            const ctx = computeDurations ? new (window.AudioContext || (window as any).webkitAudioContext)() : null;
                            const decodeWithTimeout = async (file: File, timeoutMs = 3000): Promise<number | null> => {
                              if (!ctx) return null;
                              if (file.size > 15 * 1024 * 1024) return null; // skip very large files
                              const buf = await file.arrayBuffer();
                              return await Promise.race([
                                ctx.decodeAudioData(buf.slice(0)).then((ab: AudioBuffer) => Math.round(ab.duration)),
                                new Promise<number | null>((resolve) => setTimeout(() => resolve(null), timeoutMs))
                              ]);
                            };

                            for (let i = 0; i < limit; i++) {
                              const it = items[i];
                              const docRef = doc(colRef, it.key);
                              let durationSeconds = parseDurationFromName(it.name) ?? null;
                              if (durationSeconds === null && computeDurations) {
                                try {
                                  const file = await it.handle.getFile();
                                  durationSeconds = await decodeWithTimeout(file);
                                } catch {}
                              }
                              batch.set(docRef, { id: it.key, title: it.name, artist: it.artist || null, durationSeconds, updatedAt: serverTimestamp() }, { merge: true });
                            }
                            await batch.commit();
                            alert(`Published ${limit} local songs to catalog for guests.`);
                          } catch (e) {
                            alert('Failed to publish catalog.');
                          }
                        }}
                        className="px-3 py-2 bg-green-600 hover:bg-green-700 rounded text-white"
                        title="Publish catalog for guests to browse via QR/Search"
                      >
                        Publish Catalog
                      </button>
                      <button
                        onClick={() => { setLibraryItems([]); setLibraryConnected(false); setLibraryQuery(''); }}
                        className="px-3 py-2 bg-gray-600 hover:bg-gray-700 rounded"
                      >
                        Disconnect
                      </button>
                    </div>
                    <div className="max-h-64 overflow-y-auto divide-y divide-gray-700 rounded border border-gray-700">
                      {libraryItems
                        .filter(it => {
                          const q = libraryQuery.trim().toLowerCase();
                          if (!q) return true;
                          return it.name.toLowerCase().includes(q) || (it.artist?.toLowerCase().includes(q) ?? false);
                        })
                        .slice(0, 500)
                        .map((it) => (
                          <div key={it.key} className="flex items-center justify-between p-3 bg-gray-800/60 hover:bg-gray-800">
                            <div className="min-w-0 mr-3">
                              <div className="text-white font-medium truncate">{it.name}</div>
                              <div className="text-xs text-gray-300 truncate">{it.artist ?? 'Unknown Artist'}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                className="px-3 py-1 rounded bg-green-600 hover:bg-green-700 text-sm"
                                onClick={async () => {
                                  try {
                                    const file = await it.handle.getFile();
                                    const url = URL.createObjectURL(file);
                                    const event = new CustomEvent('qroke:localTrackSelected', { detail: { url, title: it.name, artist: it.artist } });
                                    window.dispatchEvent(event);
                                    alert(`Playing local track: ${it.artist ? it.artist + ' - ' : ''}${it.name}`);
                                  } catch (e) {
                                    alert('Failed to open file.');
                                  }
                                }}
                              >
                                ▶ Play Now
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-300 text-sm">
                    Connect a folder to index .mp3/.ogg/.wav files locally. Files stay on this device.
                  </div>
                )}
              </div>

              <div className="bg-blue-900/30 border border-blue-600/50 rounded-lg p-4 text-blue-100 text-sm">
                Looking for fully licensed catalogs? See <a className="underline" href="/content-sources" target="_blank" rel="noopener">Content Sources & Licensing</a> for options.
              </div>
            </div>
          )}
          {/* Games Tab */}
          {activeTab === 'games' && (
            <div className="space-y-6">
              <div className="relative overflow-hidden rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-950 via-slate-900 to-slate-950 p-6">
                <div className="absolute -top-6 -right-4 h-32 w-32 rounded-full bg-emerald-600/20 blur-3xl" aria-hidden="true" />
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">🐸</span>
                    <div>
                      <p className="text-sm uppercase tracking-[0.3em] text-emerald-300">Frog Lab</p>
                      <h3 className="text-2xl font-semibold">Farkle Playtest Portal</h3>
                    </div>
                  </div>
                  <p className="text-gray-200 text-sm">
                    Swap your host view into a lightweight Farkle prototype without leaving the party. Perfect for testing new mini-games with a frog-forward vibe.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => launchFarklePlaytest('same-tab')}
                      disabled={!resolvedPartyId}
                      className={`px-5 py-2 rounded-lg font-semibold transition-colors ${
                        resolvedPartyId ? 'bg-emerald-500 hover:bg-emerald-400 text-slate-900' : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Switch To Farkle
                    </button>
                    <button
                      onClick={() => launchFarklePlaytest('new-tab')}
                      disabled={!resolvedPartyId}
                      className={`px-5 py-2 rounded-lg font-semibold transition-colors border ${
                        resolvedPartyId ? 'border-emerald-400 text-emerald-200 hover:bg-emerald-400/10' : 'border-gray-600 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Open In New Tab
                    </button>
                  </div>
                  <p className="text-xs text-gray-400">
                    {resolvedPartyId ? 'Playtest is tied to this party for shared context. Return via browser back to slide into QrOke again.' : 'Start or join a party to enable the games lab.'}
                  </p>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-900/20 p-4">
                  <p className="text-sm font-semibold text-emerald-200">Frog Forward Branding</p>
                  <p className="text-xs text-gray-300 mt-2">Subtle neon greens, lily pad gradients, and playful frog badges keep QrOke's identity intact while signaling an experimental zone.</p>
                </div>
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-900/20 p-4">
                  <p className="text-sm font-semibold text-emerald-200">Safe Sandbox</p>
                  <p className="text-xs text-gray-300 mt-2">The playtest route lives outside of the main queue, so your karaoke flow stays untouched while you experiment.</p>
                </div>
              </div>

              {/* Imposter Game */}
              <div className="relative overflow-hidden rounded-2xl border border-purple-500/40 bg-gradient-to-br from-purple-950 via-slate-900 to-slate-950 p-6">
                <div className="absolute -top-6 -right-4 h-32 w-32 rounded-full bg-purple-600/20 blur-3xl" aria-hidden="true" />
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">🎭</span>
                    <div>
                      <p className="text-sm uppercase tracking-[0.3em] text-purple-300">Party Games</p>
                      <h3 className="text-2xl font-semibold">Imposter Word Game</h3>
                    </div>
                  </div>
                  <p className="text-gray-200 text-sm">
                    A hidden roles game where one player doesn't know the secret word! Give clues, find the imposter, and try to blend in. Perfect for 4-10 players.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => {
                        if (resolvedPartyId) {
                          window.location.href = `/party/${resolvedPartyId}/game/imposter`;
                        }
                      }}
                      disabled={!resolvedPartyId}
                      className={`px-5 py-2 rounded-lg font-semibold transition-colors ${
                        resolvedPartyId ? 'bg-purple-500 hover:bg-purple-400 text-white' : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Launch Imposter Game
                    </button>
                    <button
                      onClick={() => {
                        if (resolvedPartyId) {
                          window.open(`/party/${resolvedPartyId}/game/imposter`, '_blank');
                        }
                      }}
                      disabled={!resolvedPartyId}
                      className={`px-5 py-2 rounded-lg font-semibold transition-colors border ${
                        resolvedPartyId ? 'border-purple-400 text-purple-200 hover:bg-purple-400/10' : 'border-gray-600 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Open In New Tab
                    </button>
                  </div>
                  <p className="text-xs text-gray-400">
                    {resolvedPartyId ? 'Players scan the QR code to join on their phones. Host controls the game flow.' : 'Start or join a party to enable games.'}
                  </p>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-purple-500/30 bg-purple-900/20 p-4">
                  <p className="text-sm font-semibold text-purple-200">Hidden Roles</p>
                  <p className="text-xs text-gray-300 mt-2">One or more imposters don't know the secret word. They must blend in while others try to spot them based on their clues.</p>
                </div>
                <div className="rounded-xl border border-purple-500/30 bg-purple-900/20 p-4">
                  <p className="text-sm font-semibold text-purple-200">Social Deduction</p>
                  <p className="text-xs text-gray-300 mt-2">Players give one-word clues, vote on the imposter, and score points. Perfect for parties and family game nights!</p>
                </div>
              </div>
            </div>
          )}
          {/* Queue Management Tab */}
          {activeTab === 'queue' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold">Queue Management</h3>
                <div className="flex items-center space-x-2">
                  {/* Splashscreen Controls moved here */}
                  <div className="flex items-center gap-2 mr-4">
                    <label className="text-white text-sm font-semibold flex items-center gap-2">
                      Splash:
                      <input
                        type="number"
                        min={0}
                        max={99}
                        value={splashCountdown}
                        onChange={e => onSplashCountdownChange(Math.max(0, Number(e.target.value)))}
                        className="w-14 text-center text-sm font-bold bg-black/60 border border-gray-500 rounded px-2 py-1 text-white"
                        title="Splashscreen seconds"
                      />
                    </label>
                    <button
                      className={`px-3 py-1 rounded bg-yellow-500 hover:bg-yellow-400 text-black text-sm font-semibold ${splashPaused ? 'opacity-70' : ''}`}
                      onClick={() => onSplashPausedChange(!splashPaused)}
                      title={splashPaused ? 'Resume Splashscreen' : 'Pause Splashscreen'}
                    >
                      {splashPaused ? 'Resume' : 'Pause'}
                    </button>
                  </div>
                  <button
                    onClick={handleShuffleQueue}
                    disabled={processingQueue || songQueue.length === 0}
                    className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 px-4 py-2 rounded text-white font-medium transition-colors"
                  >
                    🔀 Shuffle
                  </button>
                  <button
                    onClick={handleClearQueue}
                    disabled={processingQueue || songQueue.length === 0}
                    className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 px-4 py-2 rounded text-white font-medium transition-colors"
                  >
                    🗑️ Clear All
                  </button>
                  {/* Venue Ads (premium-only): manual play at next break */}
                  {subscription.isActive && subscription.tier !== 'free' ? (
                    <div className="ml-3 flex items-center gap-2 bg-purple-900/20 border border-purple-700/40 p-2 rounded">
                      <span className="text-sm">Venue Ad:</span>
                      <button
                        onClick={scheduleVenueAdNextBreak}
                        disabled={venueAdScheduled?.status === 'scheduled'}
                        className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 px-3 py-1 rounded text-white text-sm"
                      >
                        ▶ Play at Next Break
                      </button>
                      <button
                        onClick={cancelScheduledVenueAd}
                        disabled={!venueAdScheduled || venueAdScheduled.status !== 'scheduled'}
                        className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-600 px-3 py-1 rounded text-white text-sm"
                      >
                        ✕ Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="ml-3 flex items-center gap-2 bg-gray-800/60 border border-gray-700/60 p-2 rounded text-gray-400" title="Premium required">
                      <span className="text-sm">Venue Ad:</span>
                      <button
                        disabled
                        className="bg-gray-700 px-3 py-1 rounded text-white text-sm opacity-60 cursor-not-allowed"
                      >
                        ▶ Play at Next Break
                      </button>
                      <button
                        disabled
                        className="bg-gray-700 px-3 py-1 rounded text-white text-sm opacity-60 cursor-not-allowed"
                      >
                        ✕ Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Current Song */}
              {currentSong && (
                <div className="bg-green-900/30 border border-green-600/50 rounded-lg p-4">
                  <h4 className="font-semibold text-green-200 mb-2">🎵 Now Playing</h4>
                  <div className="flex items-center space-x-4">
                    {currentSong.thumbnail && (
                      <img 
                        src={currentSong.thumbnail} 
                        alt={currentSong.title}
                        className="w-16 h-12 object-cover rounded"
                      />
                    )}
                    <div className="flex-1">
                      <p className="text-white font-medium">{currentSong.title}</p>
                      <p className="text-green-200 text-sm">{currentSong.artist}</p>
                      <p className="text-green-300 text-xs">Singer: {currentSong.singerName}</p>
                    </div>
                    <div className="flex flex-col space-y-2">
                      <button
                        onClick={handleSkipSong}
                        disabled={processingQueue}
                        className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 px-3 py-1 rounded text-white text-sm transition-colors"
                      >
                        ⏭️ Skip
                      </button>
                      {(currentSong as any).videoId && (
                        <button
                          onClick={() => {
                            const reason = prompt('Report reason:\n1. Poor sync\n2. Wrong lyrics\n3. Not karaoke\n4. Low quality\n5. Inappropriate\n\nEnter 1-5:');
                            const reasons = ['poor_sync', 'wrong_lyrics', 'not_karaoke', 'low_quality', 'inappropriate'];
                            const reasonIndex = parseInt(reason || '0') - 1;
                            if (reasonIndex >= 0 && reasonIndex < 5) {
                              const notes = prompt('Additional notes (optional):') || '';
                              AnalyticsService.reportVideo(
                                (currentSong as any).videoId,
                                currentSong.title,
                                reasons[reasonIndex] as any,
                                notes,
                                'host'
                              ).then(() => alert('Video reported. Thanks for the feedback!'));
                            }
                          }}
                          className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-white text-xs transition-colors"
                          title="Report video quality issues"
                        >
                          🚩 Report
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Song Queue */}
              <div className="bg-gray-700 rounded-lg">
                <div className="p-4 border-b border-gray-600">
                  <h4 className="font-semibold text-white">
                    📋 Upcoming Songs ({songQueue.length})
                  </h4>
                </div>
                
                {songQueue.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    <div className="text-4xl mb-2">🎤</div>
                    <p>No songs in queue</p>
                    <p className="text-sm">Share the QR code for guests to add songs!</p>
                  </div>
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    {songQueue.map((song, index) => (
                      <div 
                        key={song.id}
                        className="flex items-center space-x-4 p-4 border-b border-gray-600 last:border-b-0 hover:bg-gray-600/50 transition-colors"
                      >
                        <div className="text-gray-400 font-medium min-w-[2rem]">
                          #{index + 1}
                        </div>
                        
                        {song.thumbnail && (
                          <img 
                            src={song.thumbnail} 
                            alt={song.title}
                            className="w-12 h-9 object-cover rounded"
                          />
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{song.title}</p>
                          <p className="text-gray-300 text-sm truncate">{song.artist}</p>
                          <p className="text-gray-400 text-xs">
                            Added by: {song.singerName} • {song.duration || 'Unknown duration'}
                          </p>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {index > 0 && (
                            <button
                              onClick={() => handleMoveSong(song.id, index - 1)}
                              disabled={processingQueue}
                              className="p-1 hover:bg-gray-500 rounded text-gray-300 hover:text-white transition-colors"
                              title="Move up"
                            >
                              ⬆️
                            </button>
                          )}
                          
                          {index < songQueue.length - 1 && (
                            <button
                              onClick={() => handleMoveSong(song.id, index + 1)}
                              disabled={processingQueue}
                              className="p-1 hover:bg-gray-500 rounded text-gray-300 hover:text-white transition-colors"
                              title="Move down"
                            >
                              ⬇️
                            </button>
                          )}
                          
                          <button
                            onClick={() => handleRemoveSong(song.id)}
                            disabled={processingQueue}
                            className="p-1 hover:bg-red-600 rounded text-gray-300 hover:text-white transition-colors"
                            title="Remove song"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {processingQueue && (
                <div className="bg-blue-900/30 border border-blue-600/50 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                    <span className="text-blue-200">Processing queue action...</span>
                  </div>
                </div>
              )}

              <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-200 mb-2">💡 Queue Management Tips</h4>
                <ul className="text-yellow-100 text-sm space-y-1">
                  <li>• Use ⬆️⬇️ arrows to reorder songs</li>
                  <li>• Click 🗑️ to remove inappropriate or duplicate songs</li>
                  <li>• Shuffle queue to randomize order</li>
                  <li>• Skip current song to move to next in queue</li>
                  <li>• Clear all to start fresh</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center p-6 border-t border-gray-700">
          <button
            onClick={handleEndParty}
            className="bg-red-600 hover:bg-red-700 px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            End Party
          </button>
          <button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default HostControlsModal;